/** A rectangle in viewport (client) coordinates, the space `position: fixed` uses. */
export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type Side = 'top' | 'bottom' | 'left' | 'right';

/** Space between the target and the spotlight's edge. */
export const SPOTLIGHT_PAD = 6;
/** The spotlight ring is drawn outside its box; keep it this far inside the viewport. */
export const RING_INSET = 2;
/** Card distance from the spotlight edge, not counting the arrow. */
export const CARD_OFFSET = 6;
export const ARROW_WIDTH = 16;
export const ARROW_HEIGHT = 8;
/** The card keeps this distance from the viewport edges. */
export const VIEWPORT_MARGIN = 16;

/** A clipping ancestor: its padding box and the axes its overflow cuts off. */
export interface Clip {
  box: Box;
  x: boolean;
  y: boolean;
}

export function toBox(rect: { top: number; left: number; width: number; height: number }): Box {
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

export function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === null || b === null) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

export function intersect(a: Box, b: Box): Box | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  if (right <= left || bottom <= top) return null;
  return { top, left, width: right - left, height: bottom - top };
}

export function overlaps(a: Box, b: Box): boolean {
  return intersect(a, b) !== null;
}

/**
 * The part of `rect` its overflow ancestors let through, so a table that scrolls sideways inside
 * a card is highlighted where it shows, not where it would be unclipped. A rect that is clipped
 * away entirely (scrolled out) comes back as is.
 */
export function clipBox(rect: Box, clips: readonly Clip[]): Box {
  let box: Box = rect;
  for (const clip of clips) {
    const bounds: Box = {
      left: clip.x ? clip.box.left : box.left,
      top: clip.y ? clip.box.top : box.top,
      width: clip.x ? clip.box.width : box.width,
      height: clip.y ? clip.box.height : box.height,
    };
    const next = intersect(box, bounds);
    if (!next) return rect;
    box = next;
  }
  return box;
}

/** The spotlight hole: the target plus even padding, kept inside the viewport so its ring shows. */
export function spotlightBox(target: Box, viewport: Size, pad = SPOTLIGHT_PAD): Box {
  const left = Math.max(target.left - pad, RING_INSET);
  const top = Math.max(target.top - pad, RING_INSET);
  const right = Math.min(target.left + target.width + pad, viewport.width - RING_INSET);
  const bottom = Math.min(target.top + target.height + pad, viewport.height - RING_INSET);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** Room for the card on each side of the anchor, after the gap and the viewport margin. */
export function roomAround(anchor: Box, viewport: Size, gap: number): Record<Side, number> {
  return {
    top: anchor.top - gap - VIEWPORT_MARGIN,
    bottom: viewport.height - (anchor.top + anchor.height) - gap - VIEWPORT_MARGIN,
    left: anchor.left - gap - VIEWPORT_MARGIN,
    right: viewport.width - (anchor.left + anchor.width) - gap - VIEWPORT_MARGIN,
  };
}

export interface CardPlacement {
  side: Side;
  /** Distance from the anchor edge to the card, arrow excluded; negative when the card overlaps. */
  offset: number;
  /** False when no side has room and the card is pulled onto the target to stay on screen. */
  fits: boolean;
}

/** Below this the card would touch the spotlight ring; above it a squeezed card keeps its arrow. */
const MIN_OFFSET = 2;

/**
 * Where the card goes: the preferred side (default bottom) when the card fits there, else its
 * opposite, else the two sides on the other axis. When nothing fits at the standard offset, the
 * side with the most room: with a smaller offset if that still leaves room for the arrow, else
 * with the card pulled back over the target just far enough to stay inside the viewport (a
 * target nearly as big as the screen).
 */
export function placeCard(
  anchor: Box,
  card: Size,
  viewport: Size,
  preferred: Side = 'bottom',
): CardPlacement {
  const gap = CARD_OFFSET + ARROW_HEIGHT;
  const room = roomAround(anchor, viewport, gap);
  const usableWidth = viewport.width - VIEWPORT_MARGIN * 2;
  const usableHeight = viewport.height - VIEWPORT_MARGIN * 2;
  const needed = (side: Side) => (side === 'top' || side === 'bottom' ? card.height : card.width);
  const fits = (side: Side) =>
    room[side] >= needed(side) &&
    (side === 'top' || side === 'bottom' ? card.width <= usableWidth : card.height <= usableHeight);
  const across: Side[] =
    preferred === 'top' || preferred === 'bottom' ? ['right', 'left'] : ['bottom', 'top'];
  const order: Side[] = [preferred, OPPOSITE[preferred], ...across];
  const fitting = order.find(fits);
  if (fitting) return { side: fitting, offset: CARD_OFFSET, fits: true };
  const side = order.reduce((best, next) =>
    room[next] / needed(next) > room[best] / needed(best) ? next : best,
  );
  // Space between the anchor and the card when the card is pushed against the viewport margin.
  const space = room[side] + gap - needed(side);
  if (space >= ARROW_HEIGHT + MIN_OFFSET) {
    return { side, offset: space - ARROW_HEIGHT, fits: true };
  }
  // The layer drops the arrow here (it would point from inside the target), so the arrow's room
  // goes to the card.
  return { side, offset: Math.min(CARD_OFFSET, space), fits: false };
}
